import {useState, useEffect} from 'react';
import {
  Form,
  Input,
  InputNumber,
  Select,
  Tabs,
  DatePicker,
  Tooltip,
  Switch,
} from 'antd';
import {useQuery} from '@apollo/client';
import {toast} from 'react-toastify';
import {useSelector} from 'react-redux';
import _ from 'lodash';
import {UploadCSV} from 'components/uploadCSV';
import SubmitButton from 'components/Startup/components/SubmitButton';
import {
  GET_ALL_BROKERS,
  GET_COMPANIES_IN_STOCK_EXCHANGE,
} from 'graphql/queries/stocks';
import {brokerOptionLabel} from '../brokerVenues';
import styles from './autoExecuteModal.module.scss';
import {UserSelectField} from 'components/userSelectField/UserSelectField';
import {ConfirmModal} from './HandleOrders';
import ExecutionSummaryModal from './ExecutionSummaryModal';
import {ModalShell} from './ui/ModalShell';
import theme from '../ordersTheme.module.scss';
import moment from 'moment';

const {TabPane} = Tabs;
const {Option} = Select;

export const AutoExecuteModal = ({onClose, form, onExecutionSummary}) => {
  const [internalForm] = Form.useForm();
  const formInstance = form || internalForm;
  const [activeTab, setActiveTab] = useState('SINGLE');
  const [symbolSearchTerm, setSymbolSearchTerm] = useState('');
  const [showConfirm, setShowConfirm] = useState(false);
  const [pendingValues, setPendingValues] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const loggedInUserId = useSelector(state => state?.auth?.userData?.user?.id);
  const type = Form.useWatch('type', formInstance);
  const userId = Form.useWatch('user', formInstance);
  const selectedSymbol = Form.useWatch('symbol', formInstance);
  const {data: getAllBrokers, loading: loadingAllBrokers} = useQuery(
    GET_ALL_BROKERS,
    {
      errorPolicy: 'all',
      onError: error => toast.error(error.message),
    },
  );

  const {data: stocksData, loading: loadingStocks} = useQuery(
    GET_COMPANIES_IN_STOCK_EXCHANGE,
    {
      variables: {
        stockExchangeCode: 'BRVM',
        page: 1,
        limit: 50,
        searchTerm: symbolSearchTerm,
      },
      errorPolicy: 'all',
      onError: error => toast.error(error.message),
    },
  );

  const debouncedSetSymbolSearch = _.debounce(value => {
    setSymbolSearchTerm(value);
  }, 500);

  // Reset form when switching tabs
  useEffect(() => {
    formInstance.resetFields();
  }, [activeTab, formInstance]);

  // Reset all state when modal closes
  useEffect(() => {
    return () => {
      setActiveTab('SINGLE');
      setSymbolSearchTerm('');
      if (!form) {
        internalForm.resetFields();
      }
    };
  }, [form, internalForm]);

  const stocks =
    stocksData?.adminGetStockExchangeListedCompanies?.companies || [];

  // Calculate the suggested price based on lastPrice and type
  const getCalculatedPrice = () => {
    if (selectedSymbol && stocks.length > 0) {
      const selectedStock = stocks.find(
        stock => stock?.symbol === selectedSymbol,
      );
      if (selectedStock?.lastPrice) {
        const lastPrice = selectedStock.lastPrice;
        if (type === 'MARKET') {
          return (lastPrice * 1.03).toFixed(2);
        } else {
          return lastPrice.toFixed(2);
        }
      }
    }
    return null;
  };

  const calculatedPrice = getCalculatedPrice();

  // Update limit and price fields when symbol is selected or order type changes
  useEffect(() => {
    if (selectedSymbol && stocks.length > 0) {
      const selectedStock = stocks.find(
        stock => stock?.symbol === selectedSymbol,
      );
      if (selectedStock?.lastPrice) {
        const lastPrice = selectedStock.lastPrice;
        formInstance.setFieldValue('limit', lastPrice);

        // Calculate price based on order type
        if (type === 'MARKET') {
          formInstance.setFieldValue('price', (lastPrice * 1.03).toFixed(2));
        } else {
          // LIMIT order: price equals limit
          formInstance.setFieldValue('price', lastPrice.toFixed(2));
        }
      }
    }
  }, [selectedSymbol, type, stocks, formInstance]);

  const handleSubmit = values => {
    // Store values and show confirmation screen
    setPendingValues(values);
    setShowConfirm(true);
  };

  const handleSummaryClose = () => {
    setShowExecutionSummary(false);
    setExecutionSummary(null);
    onClose();
  };

  const [executionSummary, setExecutionSummary] = useState(null);
  const [showExecutionSummary, setShowExecutionSummary] = useState(false);

  const executeOrder = async () => {
    if (!pendingValues) return;

    setIsSubmitting(true);
    try {
      if (activeTab === 'SINGLE') {
        const params = {
          id: pendingValues.id,
          symbol: pendingValues.symbol,
          quantity: Number(pendingValues.quantity),
          type: pendingValues.type,
          direction: pendingValues.direction,
          price: Number(pendingValues.price),
          limit: Number(pendingValues.limit),
          force: pendingValues.force,
          user: pendingValues.user,
          expiration: pendingValues.expiration
            ? new Date(pendingValues.expiration).toLocaleDateString('fr-FR')
            : null,
          ...(pendingValues?.broker && {broker: pendingValues.broker}),
          caller: loggedInUserId,
        };

        fetch(`https://automaton.dabafinance.com/brvm/router`, {
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${localStorage.getItem('token') || ''}`,
          },
          method: 'POST',
          body: JSON.stringify(params),
        })
          .then(async res => {
            const executionData = await res.json().catch(() => null);

            if (res.ok) {
              toast.success('Order Executed Successfully');
              formInstance.resetFields();
              setActiveTab('SINGLE');
              setSymbolSearchTerm('');
              setShowConfirm(false);
              setPendingValues(null);
              setExecutionSummary(executionData);
              setShowExecutionSummary(true);
              if (onExecutionSummary) onExecutionSummary(executionData);
            } else {
              const errorMessage =
                executionData?.response?.data ||
                executionData?.message ||
                executionData?.data ||
                'Order Execution Failed';
              toast.error(
                typeof errorMessage === 'string'
                  ? errorMessage
                  : 'Order Execution Failed',
              );
            }
          })
          .catch(err => {
            const errorMessage =
              err?.response?.data ||
              err?.message ||
              (typeof err === 'string' ? err : 'Something went wrong!!');
            toast.error(
              typeof errorMessage === 'string'
                ? errorMessage
                : `Something went wrong!! - ${err}`,
            );
          });
      } else {
        // Multiple flow - handle CSV uploads
        const params = {
          ...(pendingValues.broker && {broker: pendingValues.broker}),
          bundle: pendingValues.bundle,
          ...(pendingValues.unbundled && {unbundled: pendingValues.unbundled}),
          type: pendingValues.type,
          force: pendingValues.force || false,
          caller: loggedInUserId,
        };

        const res = await fetch(
          `https://automaton.dabafinance.com/brvm/router`,
          {
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${localStorage.getItem('token') || ''}`,
            },
            method: 'POST',
            body: JSON.stringify(params),
          },
        );
        const executionData = await res.json().catch(() => null);

        if (res.ok) {
          toast.success('Orders Executed Successfully');
          formInstance.resetFields();
          setActiveTab('SINGLE');
          setSymbolSearchTerm('');
          setShowConfirm(false);
          setPendingValues(null);
          setExecutionSummary(executionData);
          setShowExecutionSummary(true);
          if (onExecutionSummary) onExecutionSummary(executionData);
        } else {
          const errorMessage =
            executionData?.response?.data ||
            executionData?.message ||
            executionData?.data ||
            'Order Execution Failed';
          toast.error(
            typeof errorMessage === 'string'
              ? errorMessage
              : 'Order Execution Failed',
          );
        }
      }
    } catch (err) {
      const errorMessage =
        err?.response?.data ||
        err?.message ||
        (typeof err === 'string' ? err : 'Something went wrong!!');
      toast.error(
        typeof errorMessage === 'string'
          ? errorMessage
          : `Something went wrong!! - ${err.message || err}`,
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderConfirmContent = () => {
    if (!pendingValues) return null;

    if (activeTab === 'SINGLE') {
      const selectedStock = stocks.find(
        stock => stock?.symbol === pendingValues.symbol,
      );
      const selectedBroker = getAllBrokers?.getBrokers?.data?.find(
        broker => broker?.id === pendingValues.broker,
      );

      return (
        <div className={styles.confirmContent}>
          <div className={styles.confirmDetails}>
            <div className={styles.confirmRow}>
              <span className={styles.confirmLabel}>Order ID:</span>
              <span className={styles.confirmValue}>{pendingValues.id}</span>
            </div>
            <div className={styles.confirmRow}>
              <span className={styles.confirmLabel}>Symbol:</span>
              <span className={styles.confirmValue}>
                {pendingValues.symbol}
                {selectedStock && ` - ${selectedStock.company?.name}`}
              </span>
            </div>
            <div className={styles.confirmRow}>
              <span className={styles.confirmLabel}>Quantity:</span>
              <span className={styles.confirmValue}>
                {pendingValues.quantity?.toLocaleString()}
              </span>
            </div>
            <div className={styles.confirmRow}>
              <span className={styles.confirmLabel}>Type:</span>
              <span className={styles.confirmValue}>{pendingValues.type}</span>
            </div>
            <div className={styles.confirmRow}>
              <span className={styles.confirmLabel}>Direction:</span>
              <span className={styles.confirmValue}>
                {pendingValues.direction}
              </span>
            </div>
            <div className={styles.confirmRow}>
              <span className={styles.confirmLabel}>Price:</span>
              <span className={styles.confirmValue}>
                {pendingValues.price?.toLocaleString()}
              </span>
            </div>
            <div className={styles.confirmRow}>
              <span className={styles.confirmLabel}>Limit:</span>
              <span className={styles.confirmValue}>
                {pendingValues.limit?.toLocaleString()}
              </span>
            </div>
            {pendingValues.user && (
              <div className={styles.confirmRow}>
                <span className={styles.confirmLabel}>User:</span>
                <span className={styles.confirmValue}>
                  {pendingValues.user?.split(';')[0] || pendingValues.user}
                </span>
              </div>
            )}
            {pendingValues.expiration && (
              <div className={styles.confirmRow}>
                <span className={styles.confirmLabel}>Expiration:</span>
                <span className={styles.confirmValue}>
                  {moment(pendingValues.expiration).format(
                    'YYYY-MM-DD HH:mm:ss',
                  )}
                </span>
              </div>
            )}
            {selectedBroker && (
              <div className={styles.confirmRow}>
                <span className={styles.confirmLabel}>Broker:</span>
                <span className={styles.confirmValue}>
                  {selectedBroker.name} ({selectedBroker.assetClass})
                </span>
              </div>
            )}
          </div>
          <p className={styles.confirmWarning}>
            Are you sure you want to execute this order?
          </p>
        </div>
      );
    } else {
      return (
        <div className={styles.confirmContent}>
          <div className={styles.confirmDetails}>
            <div className={styles.confirmRow}>
              <span className={styles.confirmLabel}>Type:</span>
              <span className={styles.confirmValue}>{pendingValues.type}</span>
            </div>
            <div className={styles.confirmRow}>
              <span className={styles.confirmLabel}>Bundle CSV:</span>
              <span className={styles.confirmValue}>
                {pendingValues.bundle ? 'Uploaded' : 'Not provided'}
              </span>
            </div>
            {pendingValues.type === 'MARKET' && (
              <div className={styles.confirmRow}>
                <span className={styles.confirmLabel}>Unbundled CSV:</span>
                <span className={styles.confirmValue}>
                  {pendingValues.unbundled ? 'Uploaded' : 'Not provided'}
                </span>
              </div>
            )}
          </div>
          <p className={styles.confirmWarning}>
            Are you sure you want to execute these orders?
          </p>
        </div>
      );
    }
  };

  return (
    <>
      <ModalShell
        title="Execute orders"
        subtitle="Send an order straight to the router. Single executes one order you describe here; Multiple executes a CSV bundle."
        bodyClassName={theme.executeBody}>
        <Tabs activeKey={activeTab} onChange={setActiveTab}>
          <TabPane tab="Single" key="SINGLE">
            <Form
              form={formInstance}
              layout="vertical"
              onFinish={handleSubmit}
              onFinishFailed={() => {
                toast.error('Please fill in all required fields');
              }}
              className={styles.executeForm}>
              <div className={styles.rowFields}>
                <Form.Item
                  label="Id"
                  name="id"
                  className={styles.halfWidth}
                  rules={[
                    {
                      required: activeTab === 'SINGLE',
                      message: 'Id is required',
                    },
                  ]}>
                  <Input placeholder="Order ID" />
                </Form.Item>
                <Form.Item
                  label="Symbol"
                  name="symbol"
                  className={styles.halfWidth}
                  rules={[
                    {
                      required: activeTab === 'SINGLE',
                      message: 'Symbol is required',
                    },
                  ]}>
                  <Select
                    showSearch
                    filterOption={false}
                    allowClear
                    onSearch={debouncedSetSymbolSearch}
                    loading={loadingStocks}
                    placeholder="Search symbol">
                    {stocks.map(stock => (
                      <Option key={stock?.symbol} value={stock?.symbol}>
                        {stock?.symbol} - {stock?.company?.name}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </div>
              <div className={styles.rowFields}>
                <Form.Item
                  label="Quantity"
                  name="quantity"
                  className={styles.halfWidth}
                  rules={[
                    {
                      required: activeTab === 'SINGLE',
                      message: 'Quantity is required',
                    },
                  ]}>
                  <InputNumber
                    min={0}
                    className="input-number"
                    placeholder="Quantity"
                    style={{width: '100%'}}
                  />
                </Form.Item>
                <Form.Item
                  label="Type"
                  name="type"
                  className={styles.halfWidth}
                  rules={[
                    {
                      required: activeTab === 'SINGLE',
                      message: 'Type is required',
                    },
                  ]}>
                  <Select placeholder="Select Type">
                    <Option value="LIMIT">Limit</Option>
                    <Option value="MARKET">Market</Option>
                  </Select>
                </Form.Item>
              </div>
              <div className={styles.rowFields}>
                <Form.Item
                  label="Limit"
                  name="limit"
                  className={styles.halfWidth}
                  rules={[
                    {
                      required: activeTab === 'SINGLE',
                      message: 'Limit is required',
                    },
                  ]}>
                  <InputNumber
                    min={0}
                    placeholder="Limit Price"
                    className="input-number"
                    style={{width: '100%'}}
                  />
                </Form.Item>

                <Form.Item
                  label={
                    type === 'MARKET' && selectedSymbol ? (
                      <span>
                        Price{' '}
                        <Tooltip title="For MARKET orders, price is calculated as Last Price × 1.03">
                          <span style={{color: '#8d6cfa', cursor: 'help'}}>
                            (Limit Price × 1.03)
                          </span>
                        </Tooltip>
                      </span>
                    ) : (
                      'Price'
                    )
                  }
                  name="price"
                  className={styles.halfWidth}
                  rules={[
                    {
                      required: activeTab === 'SINGLE',
                      message: 'Price is required',
                    },
                  ]}
                  extra={
                    calculatedPrice && selectedSymbol
                      ? type === 'MARKET'
                        ? `Calculated: ${calculatedPrice} (Last Price × 1.03 for MARKET orders)`
                        : `Calculated: ${calculatedPrice} (Last Price for LIMIT orders)`
                      : null
                  }>
                  <InputNumber
                    min={0}
                    className="input-number"
                    placeholder="Price"
                    style={{width: '100%'}}
                  />
                </Form.Item>
              </div>
              <div className={styles.rowFields}>
                <Form.Item
                  label="Direction"
                  name="direction"
                  className={styles.halfWidth}
                  rules={[
                    {
                      required: activeTab === 'SINGLE',
                      message: 'Direction is required',
                    },
                  ]}>
                  <Select placeholder="Select Direction">
                    <Option value="BUY">Buy</Option>
                    <Option value="SELL">Sell</Option>
                  </Select>
                </Form.Item>
                <Form.Item
                  label="User"
                  name="user"
                  className={styles.halfWidth}>
                  <UserSelectField
                    mode="single"
                    users={userId}
                    setUsers={value =>
                      formInstance.setFieldValue('user', value)
                    }
                    placeholder="Select User"
                  />
                </Form.Item>
              </div>

              <div className={styles.rowFields}>
                <Form.Item
                  label="Expiration"
                  name="expiration"
                  className={styles.halfWidth}>
                  <DatePicker
                    showTime
                    format="YYYY-MM-DD HH:mm:ss"
                    style={{width: '100%'}}
                    placeholder="Select expiration date"
                  />
                </Form.Item>
                <Form.Item
                  label="Select Broker (Optional)"
                  name="broker"
                  className={styles.halfWidth}>
                  <Select
                    allowClear
                    showSearch
                    loading={loadingAllBrokers}
                    placeholder="Select a broker">
                    {getAllBrokers?.getBrokers?.data?.map(broker => (
                      <Option key={broker?.id} value={broker?.id}>
                        {brokerOptionLabel(broker)}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </div>
              <Form.Item
                label="Force"
                name="force"
                initialValue={false}
                tooltip="ensures that an order is executed irrespective of the router's decision e.g force a buy order placement even when a sell order for the same stock already exists on the same broker"
                className={styles.halfWidth}>
                <Switch />
              </Form.Item>
              <div className={styles.buttons}>
                <SubmitButton label="Execute Order" />
              </div>
            </Form>
          </TabPane>
          <TabPane tab="Multiple" key="MULTIPLE">
            <Form
              form={formInstance}
              layout="vertical"
              onFinish={handleSubmit}
              onFinishFailed={errorInfo => {
                console.log('Form validation failed:', errorInfo);
                toast.error('Please fill in all required fields');
              }}
              className={styles.executeForm}>
              <Form.Item
                label="Select Broker (Optional)"
                name="broker"
                className={styles.halfWidth}>
                <Select
                  allowClear
                  style={{width: '50%'}}
                  showSearch
                  loading={loadingAllBrokers}
                  placeholder="Select a broker">
                  {getAllBrokers?.getBrokers?.data?.map(broker => (
                    <Option key={broker?.id} value={broker?.id}>
                      {brokerOptionLabel(broker)}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
              <Form.Item
                name="type"
                label="Type"
                rules={[
                  {
                    required: activeTab === 'MULTIPLE',
                    message: 'Type is required',
                  },
                ]}>
                <Select style={{width: '50%'}}>
                  <Option value="LIMIT">Limit</Option>
                  <Option value="MARKET">Market</Option>
                </Select>
              </Form.Item>
              <Form.Item
                name="bundle"
                label="Bundle"
                rules={[
                  {
                    required: activeTab === 'MULTIPLE',
                    message: 'Bundle is required',
                  },
                ]}
                trigger="onChange"
                getValueFromEvent={v => v}
                valuePropName="value">
                <UploadCSV
                  clear={() => formInstance.setFieldsValue({bundle: undefined})}
                  sampleLink="https://dabacdn-bucket001.s3.us-east-1.amazonaws.com/contents/bundled.csv"
                />
              </Form.Item>
              {type === 'MARKET' && (
                <Form.Item
                  name="unbundled"
                  label="Unbundled"
                  rules={[
                    {
                      required: activeTab === 'MULTIPLE' && type === 'MARKET',
                      message: 'Unbundled is required',
                    },
                  ]}
                  trigger="onChange"
                  getValueFromEvent={v => v}
                  valuePropName="value">
                  <UploadCSV
                    clear={() =>
                      formInstance.setFieldsValue({unbundled: undefined})
                    }
                    sampleLink="https://dabacdn-bucket001.s3.us-east-1.amazonaws.com/contents/unbundled.csv"
                  />
                </Form.Item>
              )}
              <Form.Item
                label="Force"
                name="force"
                initialValue={false}
                tooltip="ensures that an order is executed irrespective of the router's decision e.g force a buy order placement even when a sell order for the same stock already exists on the same broker"
                className={styles.halfWidth}>
                <Switch />
              </Form.Item>
              <div className={styles.buttons}>
                <SubmitButton label="Execute Orders" />
              </div>
            </Form>
          </TabPane>
        </Tabs>
      </ModalShell>
      <ConfirmModal
        title="Confirm execution"
        openModal={showConfirm}
        setOpenModal={setShowConfirm}
        confirmText={renderConfirmContent()}
        loading={isSubmitting}
        handleOperation={executeOrder}
        confirmation={true}
      />
      <ExecutionSummaryModal
        open={showExecutionSummary}
        data={executionSummary}
        onClose={handleSummaryClose}
      />
    </>
  );
};
